/**
 * Agent Lifecycle Panel — operational memory visibility (feedback + learnings)
 */

import { useState, useEffect } from 'react';
import {
  Brain,
  MessageSquare,
  BookOpen,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cpFetch } from '@/lib/cpClient';
import { formatRelativeTime } from '@/pages/ApiObserve/helpers';

interface Props {
  agentId: string;
  agent: any; // TODO: type from agent registry schema
}

interface FeedbackSummary {
  total: number;
  by_type: Array<{ _id: string; count: number }>;
  recent: Array<{
    id?: string;
    feedback_type: string;
    category?: string;
    detail?: string;
    created_at?: string | Date;
  }>;
}

const feedbackTypeBadge: Record<string, string> = {
  positive: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  negative: 'bg-red-100 text-red-700 border-red-200',
  correction: 'bg-amber-100 text-amber-700 border-amber-200',
};

function confidenceDot(confidence: number | undefined): string {
  const c = typeof confidence === 'number' ? confidence : 0;
  if (c >= 0.8) return 'bg-emerald-500';
  if (c >= 0.6) return 'bg-amber-500';
  return 'bg-red-500';
}

function statusBadgeClass(status: string | undefined): string {
  if (status === 'active') return 'bg-emerald-100 text-emerald-700';
  if (status === 'pending_approval') return 'bg-amber-100 text-amber-700';
  return 'bg-gray-100 text-gray-600';
}

export function AgentLifecyclePanel({ agentId, agent }: Props) {
  const [feedbackSummary, setFeedbackSummary] = useState<FeedbackSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const learnings = Array.isArray(agent?.operational_memory?.learnings)
    ? agent.operational_memory.learnings
    : [];
  const memoryLastUpdated = agent?.operational_memory?.last_updated;
  const activeLearnings = learnings.filter((l: any) => l.status === 'active');

  useEffect(() => {
    loadFeedbackSummary();
  }, [agentId]);

  async function loadFeedbackSummary() {
    setLoading(true);
    setError(null);
    try {
      const res = await cpFetch(`/api/agents/${agentId}/feedback/summary?limit=10`);
      const payload = res?.data ?? res;
      setFeedbackSummary({
        total: payload?.total ?? 0,
        by_type: payload?.by_type ?? [],
        recent: payload?.recent ?? [],
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-400">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        <span className="text-gray-500">Loading lifecycle data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-500 text-sm">{error}</div>
    );
  }

  const totalFeedback = feedbackSummary?.total ?? 0;
  const recentFeedback = feedbackSummary?.recent ?? [];

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Stat strip */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="bg-violet-50 border-0 shadow-sm">
          <CardContent className="p-3.5 flex items-center gap-3">
            <MessageSquare className="w-7 h-7 text-violet-600 opacity-80" />
            <div>
              <p className="text-lg font-bold text-gray-900 leading-none">{totalFeedback}</p>
              <p className="text-[10px] text-gray-500 font-medium mt-0.5">Total Feedback</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50 border-0 shadow-sm">
          <CardContent className="p-3.5 flex items-center gap-3">
            <BookOpen className="w-7 h-7 text-emerald-600 opacity-80" />
            <div>
              <p className="text-lg font-bold text-gray-900 leading-none">{activeLearnings.length}</p>
              <p className="text-[10px] text-gray-500 font-medium mt-0.5">Active Learnings</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-0 shadow-sm">
          <CardContent className="p-3.5 flex items-center gap-3">
            <Brain className="w-7 h-7 text-blue-600 opacity-80" />
            <div>
              <p className="text-lg font-bold text-gray-900 leading-none">
                {memoryLastUpdated
                  ? formatRelativeTime(
                      typeof memoryLastUpdated === 'string'
                        ? memoryLastUpdated
                        : new Date(memoryLastUpdated).toISOString()
                    )
                  : 'Never'}
              </p>
              <p className="text-[10px] text-gray-500 font-medium mt-0.5">Last Memory Update</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Feedback */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between">
            <span className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-violet-600" />
              Feedback
            </span>
            <Button variant="outline" size="sm" onClick={loadFeedbackSummary}>
              <RefreshCw className="w-3.5 h-3.5 mr-1" />
              Refresh
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentFeedback.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Brain className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p className="font-medium text-gray-700">No feedback yet</p>
              <p className="text-sm mt-1">Feedback accumulates as this agent runs</p>
            </div>
          ) : (
            <div className="space-y-2">
              {recentFeedback.map((entry, idx) => {
                const type = String(entry.feedback_type || 'unknown');
                const badgeClass = feedbackTypeBadge[type] || 'bg-gray-100 text-gray-600';
                const createdAt = entry.created_at
                  ? formatRelativeTime(
                      typeof entry.created_at === 'string'
                        ? entry.created_at
                        : new Date(entry.created_at).toISOString()
                    )
                  : '—';
                return (
                  <div
                    key={entry.id || idx}
                    className="flex items-center gap-3 p-3 rounded-lg border bg-white"
                  >
                    <Badge className={`text-xs shrink-0 ${badgeClass}`}>{type}</Badge>
                    <span className="text-sm text-gray-700 truncate max-w-xs flex-1 min-w-0" title={entry.category}>
                      {entry.category || '—'}
                    </span>
                    {entry.detail ? (
                      <span className="text-xs text-gray-500 truncate max-w-[200px] hidden sm:inline" title={entry.detail}>
                        {entry.detail}
                      </span>
                    ) : null}
                    <span className="text-xs text-gray-400 shrink-0 ml-auto">{createdAt}</span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Learnings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-violet-600" />
            Learnings
          </CardTitle>
        </CardHeader>
        <CardContent>
          {learnings.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <BookOpen className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              {totalFeedback > 0 ? (
                <>
                  <p className="font-medium text-gray-700">Building learnings</p>
                  <p className="text-sm mt-1 max-w-md mx-auto">
                    {totalFeedback} feedback {totalFeedback === 1 ? 'entry' : 'entries'} collected.
                    Learnings appear after 10+ entries from 3+ tenants.
                  </p>
                </>
              ) : (
                <>
                  <p className="font-medium text-gray-700">No learnings yet</p>
                  <p className="text-sm mt-1">Learnings are distilled from agent feedback over time.</p>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {learnings.map((learning: any, idx: number) => {
                const confidence =
                  typeof learning.confidence === 'number' ? learning.confidence : null;
                const createdAt = learning.created_at || learning.last_updated;
                return (
                  <div key={learning.id || idx} className="border rounded-lg p-4 bg-white">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        {learning.category ? (
                          <Badge className="text-xs bg-violet-100 text-violet-700">{learning.category}</Badge>
                        ) : null}
                        {learning.status ? (
                          <Badge className={`text-xs ${statusBadgeClass(learning.status)}`}>
                            {learning.status}
                          </Badge>
                        ) : null}
                      </div>
                      {confidence !== null && (
                        <div className="flex items-center gap-1.5 text-xs text-gray-600 shrink-0">
                          <span className={`w-2 h-2 rounded-full ${confidenceDot(confidence)}`} />
                          {Math.round(confidence * 100)}%
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-700">{learning.insight || learning.detail || '—'}</p>
                    {createdAt && (
                      <p className="text-xs text-gray-400 mt-2">
                        {formatRelativeTime(
                          typeof createdAt === 'string' ? createdAt : new Date(createdAt).toISOString()
                        )}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
